import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import BaseBottomSheetPopup from '../../components/BasicComponent/BaseBottomSheetPopup'
import BaseButton from '../../components/BasicComponent/BaseButton'
import LoadingScreen from '../../components/BasicComponent/LoadingScreen'
import SafeImage from '../../components/BasicComponent/SafeImage'
import { IconCheckCircleGreen, IconGoiDichVuGiaTien, IconGoiDichVuSoLuongXe, IconGoiDichVuSoLuongXeMay, IconInfoCircle } from '../../assets/icons'
import { TRAFFIC_FINE_BENEFITS } from '../../components/FeatureComponent/RegisteredPackageInfo'
import { VEHICLE_TYPE } from '../../constants/global'
import { PATH } from '../../constants/router'
import { formatTwoDigitValue, number_to_price } from '../../helper/common'
import userProductService from '../../services/userProductService'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import HeaderPartner from '../../components/BasicComponent/HeaderPartner'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import PackageVehicleItem from './components/PackageVehicleItem'
import './index.scss'

const SERVICE_NOTIFY_CRIMINAL = 9
const TRAFFIC_FINE_BENEFITS_MAP = TRAFFIC_FINE_BENEFITS.reduce((result, item) => {
  result[String(item.key)] = item
  return result
}, {})

function isCarProduct(vehicleType) {
  return Number(vehicleType) === VEHICLE_TYPE.CAR
}

function isMotorbikeProduct(vehicleType) {
  return [VEHICLE_TYPE.MOTOBIKE, VEHICLE_TYPE.ELECTRIC_MOTORBIKE].includes(Number(vehicleType))
}

function getProductSortOrder(product) {
  if (isCarProduct(product?.vehicleType)) {
    return 0
  }

  return Number(product?.productSize) > 1 ? 2 : 1
}

function sortProducts(products = []) {
  return [...products].sort((productA, productB) => {
    const sortOrderA = getProductSortOrder(productA)
    const sortOrderB = getProductSortOrder(productB)

    if (sortOrderA !== sortOrderB) {
      return sortOrderA - sortOrderB
    }

    const priorityA = Number(productA?.productPriority) || 0
    const priorityB = Number(productB?.productPriority) || 0

    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    return Number(productA?.productId) - Number(productB?.productId)
  })
}

function filterVisibleProducts(products = []) {
  return products.filter((product) => {
    if (Number(product?.productCategory) !== SERVICE_NOTIFY_CRIMINAL) {
      return false
    }

    if (Number(product?.isDeleted) === 1) {
      return false
    }

    return Number(product?.isVisible ?? 1) === 1
  })
}

function getBenefits(productOptionalInfo) {
  if (!productOptionalInfo) {
    return []
  }

  if (typeof productOptionalInfo === 'object' && !Array.isArray(productOptionalInfo)) {
    const benefits = productOptionalInfo?.benefits
    return Array.isArray(benefits) ? benefits.map(Number).filter((item) => !Number.isNaN(item)) : []
  }

  try {
    const parsedInfo = JSON.parse(productOptionalInfo)
    const benefits = parsedInfo?.benefits
    return Array.isArray(benefits) ? benefits.map(Number).filter((item) => !Number.isNaN(item)) : []
  } catch {
    return []
  }
}

function isSameVehicleGroup(sourceVehicleType, targetVehicleType) {
  if (isCarProduct(sourceVehicleType)) {
    return isCarProduct(targetVehicleType)
  }

  if (isMotorbikeProduct(sourceVehicleType)) {
    return isMotorbikeProduct(targetVehicleType)
  }

  return Number(sourceVehicleType) === Number(targetVehicleType)
}

function getDefaultSelectedProduct(products = [], preferredVehicleType) {
  if (!products.length) {
    return null
  }

  if (preferredVehicleType) {
    const exactMatchedProduct = products.find((product) => Number(product?.vehicleType) === Number(preferredVehicleType))

    if (exactMatchedProduct) {
      return exactMatchedProduct
    }

    const matchedProduct = products.find((product) => isSameVehicleGroup(product?.vehicleType, preferredVehicleType))

    if (matchedProduct) {
      return matchedProduct
    }
  }

  return products[0]
}

function getVehicleCountText(product) {
  const vehicleCount = Number(product?.productSize) || 1
  const vehicleLabel = isCarProduct(product?.vehicleType) ? 'ô tô' : 'xe máy'

  return `${formatTwoDigitValue(vehicleCount)} ${vehicleLabel}`
}

function getProductPriceText(product) {
  const productPrice = Number(product?.productPriceAfterVAT || product?.productPrice || 0)
  return `${number_to_price(productPrice)}đ/tháng`
}

function getProductBenefitItems(benefitIds = []) {
  return benefitIds.reduce((result, benefitId) => {
    const benefit = TRAFFIC_FINE_BENEFITS_MAP[String(benefitId)]
    const benefitIcon = benefit?.icon2 || benefit?.icon
    const benefitText = benefit?.text2 || benefit?.text

    if (!benefitText) {
      return result
    }

    result.push({
      key: `benefit-${benefit.key}`,
      icon: benefitIcon ? <SafeImage src={benefitIcon} aria-hidden="true" /> : null,
      label: benefitText,
      isActive: true
    })

    return result
  }, [])
}

export default function TrafficFineNotificationServices() {
  const history = useHistory()
  const location = useLocation()
  const locationState = location.state || {}
  const preferredVehicleIdentity = locationState?.trafficFineRegistrationVehicle?.vehicleIdentity || ''
  const preferredVehicleType = locationState?.trafficFineRegistrationVehicle?.vehicleType
    ? Number(locationState.trafficFineRegistrationVehicle.vehicleType)
    : undefined
  const preferredProductVehicleType = isMotorbikeProduct(preferredVehicleType) ? VEHICLE_TYPE.MOTOBIKE : preferredVehicleType
  const preferredProductId = locationState?.trafficFineRegistrationProduct?.productId

  const [products, setProducts] = useState([])
  const [selectedProductId, setSelectedProductId] = useState(null)
  const [isLoadingProducts, setIsLoadingProducts] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const [isRenewalInfoPopupOpen, setIsRenewalInfoPopupOpen] = useState(false)

  const handleRetryLoadProducts = useCallback(() => {
    setReloadTick((tick) => tick + 1)
  }, [])

  useEffect(() => {
    let isMounted = true

    async function fetchProducts() {
      try {
        setIsLoadingProducts(true)
        setHasLoadError(false)

        const response = await userProductService.PartnerAPIProductUserGetList({
          filter: {
            productCategory: [SERVICE_NOTIFY_CRIMINAL]
          }
        })

        if (!isMounted) {
          return
        }

        if (response?.statusCode !== 200) {
          setProducts([])
          setHasLoadError(true)
          return
        }

        const productList = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : []

        const nextProducts = sortProducts(filterVisibleProducts(productList))
        setProducts(nextProducts)
        setHasLoadError(false)
      } catch {
        if (!isMounted) {
          return
        }

        setProducts([])
        setHasLoadError(true)
      } finally {
        if (isMounted) {
          setIsLoadingProducts(false)
        }
      }
    }

    fetchProducts()

    return () => {
      isMounted = false
    }
  }, [reloadTick])

  useEffect(() => {
    if (!products.length) {
      setSelectedProductId(null)
      return
    }

    const selectedProductExists = products.some((product) => Number(product?.productId) === Number(selectedProductId))

    if (selectedProductExists) {
      return
    }

    const preferredProduct = products.find((product) => Number(product?.productId) === Number(preferredProductId))
    const defaultProduct = preferredProduct || getDefaultSelectedProduct(products, preferredProductVehicleType)
    setSelectedProductId(defaultProduct?.productId ?? null)
  }, [preferredProductId, preferredProductVehicleType, products, selectedProductId])

  const selectedProduct = useMemo(() => {
    return products.find((product) => Number(product?.productId) === Number(selectedProductId)) || null
  }, [products, selectedProductId])

  const selectedBenefits = useMemo(() => getBenefits(selectedProduct?.productOptionalInfo), [selectedProduct])

  const productDetailItems = useMemo(() => {
    if (!selectedProduct) {
      return []
    }

    return [
      {
        key: 'price',
        icon: <IconGoiDichVuGiaTien />,
        label: 'Giá gói/tháng',
        value: getProductPriceText(selectedProduct),
        subValue: 'Tự động gia hạn hàng tháng'
      },
      {
        key: 'vehicle-count',
        icon: isMotorbikeProduct(selectedProduct?.vehicleType) ? <IconGoiDichVuSoLuongXeMay /> : <IconGoiDichVuSoLuongXe />,
        label: 'Số lượng xe',
        value: getVehicleCountText(selectedProduct)
      },
      ...getProductBenefitItems(selectedBenefits)
    ]
  }, [selectedBenefits, selectedProduct])

  const handleNextStep = () => {
    if (!selectedProduct) {
      return
    }

    const trafficFineRegistrationVehicle = {
      vehicleIdentity: preferredVehicleIdentity || undefined,
      vehicleType: preferredVehicleType
    }
    const nextLocationState = {
      ...locationState,
      trafficFineRegistrationVehicle,
      trafficFineRegistrationProduct: selectedProduct
    }

    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: nextLocationState
    })

    history.push({
      pathname: PATH.TRAFFIC_FINE_VEHICLES,
      state: {
        trafficFineRegistrationVehicle,
        trafficFineRegistrationProduct: selectedProduct
      }
    })
  }

  return (
    <LayoutPartner>
      <HeaderPartner title="Các gói dịch vụ" />
      <div className="TrafficFineNotificationServices">
        {hasLoadError && !isLoadingProducts ? (
          <div className="TrafficFineNotificationServices_empty">{'Đã xảy ra lỗi, vui lòng thử lại sau'}</div>
        ) : (
          <>
            <div className="TrafficFineNotificationServices_section">
              <div className="TrafficFineNotificationServices_title">Chọn gói đăng ký</div>
              {!isLoadingProducts && products.length ? (
                <div className="TrafficFineNotificationServices_packageList">
                  {products.map((product) => (
                    <PackageVehicleItem
                      key={product.productId}
                      product={product}
                      active={Number(product?.productId) === Number(selectedProductId)}
                      onClick={() => setSelectedProductId(product.productId)}
                    />
                  ))}
                </div>
              ) : !isLoadingProducts ? (
                <div className="TrafficFineNotificationServices_empty">{'Chưa có gói dịch vụ phù hợp.'}</div>
              ) : null}
            </div>

            {selectedProduct ? (
              <div className="TrafficFineNotificationServices_detailList">
                {productDetailItems.map((item) => (
                  <div
                    key={item.key}
                    className={`TrafficFineNotificationServices_detailItem ${
                      item.subValue ? 'TrafficFineNotificationServices_detailItem_subValue' : ''
                    }`.trim()}>
                    <div className="TrafficFineNotificationServices_detailLeft">
                      <div className="TrafficFineNotificationServices_detailIcon">{item.icon}</div>
                      <div className="TrafficFineNotificationServices_detailLabel">{item.label}</div>
                    </div>

                    {item.value ? (
                      <div className="TrafficFineNotificationServices_detailValueWrap">
                        <div className="TrafficFineNotificationServices_detailValue">{item.value}</div>
                        {item.subValue ? (
                          <button
                            type="button"
                            className="TrafficFineNotificationServices_detailSubValue"
                            aria-label="Xem thông tin nguồn tiền gia hạn"
                            // onClick={() => setIsRenewalInfoPopupOpen(true)}
                          >
                            <span>{item.subValue}</span>
                            {/* <IconInfoCircle className="TrafficFineNotificationServices_detailInfoIcon" /> */}
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <div className="TrafficFineNotificationServices_detailStatus">
                        {item.isActive ? (
                          <IconCheckCircleGreen className="TrafficFineNotificationServices_detailStatusIcon" />
                        ) : (
                          <span className="TrafficFineNotificationServices_detailStatusPlaceholder" />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>

      <FixedBottom elementPaddingBottom="LayoutPartner">
        <div className="TrafficFineNotificationServices_bottom">
          {hasLoadError && !isLoadingProducts ? (
            <BaseButton onClick={handleRetryLoadProducts}>Thử lại</BaseButton>
          ) : (
            <BaseButton disabled={!selectedProduct} onClick={handleNextStep}>
              Tiếp theo
            </BaseButton>
          )}
        </div>
      </FixedBottom>
      <LoadingScreen open={isLoadingProducts} />
      <BaseBottomSheetPopup
        open={isRenewalInfoPopupOpen}
        onClose={() => setIsRenewalInfoPopupOpen(false)}
        title="Nguồn tiền gia hạn"
        className="TrafficFineNotificationServices_renewalPopup">
        <div className="TrafficFineNotificationServices_renewalPopupContent">
          <IconInfoCircle className="TrafficFineNotificationServices_renewalPopupIcon" />
          <div className="TrafficFineNotificationServices_renewalPopupText">
            <p>Tự động gia hạn gói khi hết chu kỳ.</p>
            <p>
              Trong đó, nguồn tiền gia hạn gói bao gồm: ViettelPay, Tiền di động, các ngân hàng liên kết trực tiếp với Viettel
              Money (tuỳ thuộc chính sách, quy định của từng ngân hàng).
            </p>
            <p>Để dừng gia hạn, bạn vui lòng truy cập vào mục "Quản lý đăng ký" và huỷ gia hạn tự động.</p>
          </div>
        </div>
      </BaseBottomSheetPopup>
    </LayoutPartner>
  )
}
